import { inject } from "fw";
import { Store, handle, dispatch } from "fw-state";

import { LogoutAction, StartAction } from "./actions";
import { CurrentAtsApplyTagAction } from "./current-ats-organization-summary";
import { SessionStorageCache } from "caching";
import { once, resetAllOnce } from "helpers/once";
import { AtsRepository } from "network/ats-repository";
import { AtsOrgSummaryRepository } from "network/ats-org-summary-repository";
import { AtsOrganizationSummary } from "models/ats-organization-summary";
import { EntitySelection, AccountType, AtsOrganizationFilter } from "models/ats-organization";
import { CreateOrganizationAction, UpdatedOrganizationAction, UpdateOrganizationAction } from "forms/organization";
import { FormErrorHandling } from "./error-handling";
import { LoadSeasonConfigurationAction } from "./ats-organization-templates";

const MAX_SKIP = 2000;
const DEFAULT_PAGE_SIZE = 20;

interface AtsOrganizationShape {
    currentUserId: string;
    seasonConfiguration: any;

    loaded: boolean;
    list: AtsOrganizationSummary[];
    total: number;
    filter: AtsOrganizationFilter;
    search: string;
    sort: string;
    selectAll: boolean;
    hasErrors: boolean;
    hasMappings: boolean;

    pageSize: number;
    currentPage: number;
    lastPage: number;
    pageStartNumber: number;
    pageEndNumber: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    canAdvancePage: boolean;
}

export class AtsOrganizationBustCacheAction {}
export class AtsOrganizationEnsureAction {}
export class AtsOrganizationRefreshAction {}
export class AtsOrganizationUpdateAction {
    constructor(
        public filter: AtsOrganizationFilter = null,
        public search: string = null,
        public sort: string = null,
        public hasErrors: boolean = false,
        public hasMappings: boolean = false
    ) {}
}

export class AtsOrganizationTagAction {
    public taskId: string = null;

    constructor(
        public selection: EntitySelection,
        public addTags: string[],
        public removeTags: string[],
        public instant: boolean = false
    ) {}
}

export class AtsOrganizationNextPageAction {}
export class AtsOrganizationPreviousPageAction {}

type SavedAtsOrganizationState = {
    filter: AtsOrganizationFilter;
    search: string;
    hasErrors: boolean;
    hasMappings: boolean;
};

@inject
export class AtsOrganizationStore extends Store<AtsOrganizationShape> {
    constructor(
        private sessionCache: SessionStorageCache,
        private atsOrgSummaryRepo: AtsOrgSummaryRepository,
        private atsRepo: AtsRepository
    ) {
        super();
    }

    defaultState() {
        return {
            currentUserId: null,
            seasonConfiguration: null,

            loaded: false,
            list: [],
            total: 0,
            filter: null,
            search: null,
            sort: null,
            selectAll: false,
            hasErrors: false,
            hasMappings: false,

            pageSize: DEFAULT_PAGE_SIZE,
            currentPage: 1,
            lastPage: 1,
            pageStartNumber: 1,
            pageEndNumber: DEFAULT_PAGE_SIZE,
            hasPreviousPage: false,
            hasNextPage: false,
            canAdvancePage: false
        };
    }

    get cacheKey(): string {
        return `manager-user:${this.state.currentUserId}:ats-org-store`;
    }

    saveState() {
        if (this.state.currentUserId == null) return;

        const saveState: SavedAtsOrganizationState = {
            filter: this.state.filter,
            search: this.state.search,
            hasErrors: this.state.hasErrors,
            hasMappings: this.state.hasMappings
        };
        this.sessionCache.set<SavedAtsOrganizationState>(this.cacheKey, saveState);
    }

    restoreState() {
        if (this.state.currentUserId == null) return;

        const savedType = this.sessionCache.get<SavedAtsOrganizationState>(this.cacheKey);
        if (savedType != null) {
            this.setState((state) => ({
                ...this.defaultState(),
                currentUserId: this.state.currentUserId,
                filter: savedType.filter,
                search: savedType.search,
                hasErrors: savedType.hasErrors,
                hasMappings: savedType.hasMappings
            }));
        }
    }

    @handle(StartAction)
    private handleStartAction(action: StartAction) {
        this.setState(() => ({
            ...this.defaultState(),
            currentUserId: action.user.Id
        }));
    }

    @handle(LogoutAction)
    private handleLogoutAction() {
        this.setState(() => this.defaultState());
    }

    @handle(AtsOrganizationBustCacheAction)
    private async AtsOrganizationBustCacheAction() {
        this.setState((state) => ({
            ...state,
            loaded: false
        }));

        resetAllOnce();
        await this.handleAtsOrganizationEnsureAction();
    }

    @handle(AtsOrganizationEnsureAction)
    private async handleAtsOrganizationEnsureAction() {
        await once("ensure-ats-organizations", async () => {
            if (this.state.loaded) return;
            this.restoreState();
            this.update();
        });
    }

    @handle(AtsOrganizationRefreshAction)
    private handelRefreshAction(action: AtsOrganizationRefreshAction) {
        if (!this.state.loaded) return;

        this.setState((state) => ({
            ...state,
            currentUserId: this.state.currentUserId,
            loaded: false
        }));

        this.update();
    }

    @handle(AtsOrganizationUpdateAction)
    private handleUpdateAction(action: AtsOrganizationUpdateAction) {
        this.setState(() => ({
            ...this.defaultState(),
            currentUserId: this.state.currentUserId,
            filter: action.filter,
            search: action.search,
            sort: action.sort,
            hasErrors: action.hasErrors,
            hasMappings: action.hasMappings,
            loaded: false
        }));

        this.update();
    }

    @handle(AtsOrganizationNextPageAction)
    private handleNextPageAction(action: AtsOrganizationNextPageAction) {
        const { canAdvancePage, currentPage } = this.state;
        this.setState((state) => ({
            ...state,
            currentUserId: this.state.currentUserId,
            currentPage: canAdvancePage ? currentPage + 1 : currentPage,
            loaded: false
        }));

        this.update();
    }

    @handle(AtsOrganizationPreviousPageAction)
    private handlePreviousPageAction(action: AtsOrganizationPreviousPageAction) {
        const { currentPage } = this.state;
        this.setState((state) => ({
            ...state,
            currentUserId: this.state.currentUserId,
            currentPage: currentPage > 0 ? currentPage - 1 : currentPage,
            loaded: false
        }));

        this.update();
    }

    private async update() {
        if (this.state.loaded) return;

        const { hasErrors, hasMappings, search, sort, pageSize } = this.state;
        let { currentPage } = this.state;

        const res =
            hasErrors || hasMappings
                ? await this.atsOrgSummaryRepo.searchWithMappings(hasErrors, this.getAtsOrganizationFilter(), search, sort, currentPage, pageSize)
                : await this.atsOrgSummaryRepo.search(this.getAtsOrganizationFilter(), search, sort, currentPage, pageSize);
        const total = res.total;

        const lastPage = Math.ceil(total / pageSize);
        currentPage = Math.min(currentPage, lastPage);

        const pageStartNumber = (currentPage - 1) * pageSize + 1;
        const pageEndNumber = pageStartNumber + (pageSize - 1);
        const hasNextPage = pageEndNumber < total;
        const hasPreviousPage = pageStartNumber > 1;
        const canAdvancePage = pageEndNumber + 1 < MAX_SKIP;

        this.setState((state) => ({
            ...state,
            currentUserId: this.state.currentUserId,
            loaded: true,
            list: res.list,
            total,
            currentPage,
            pageStartNumber: Math.max(pageStartNumber, 0),
            pageEndNumber: Math.min(pageEndNumber, total),
            hasNextPage,
            hasPreviousPage,
            canAdvancePage,
            lastPage
        }));

        this.saveState();
    }

    @handle(CreateOrganizationAction, FormErrorHandling)
    private async handleCreateOrganization(action: CreateOrganizationAction) {
        const { Domain, Name, Timezone, Type, Tags, SalesForceId, ReferencePortalDomain, SeasonName, PropertyValues } = action.form;

        action.created = await this.atsRepo.postOrganization({
            Domain,
            ReferencePortalDomain,
            Name,
            Timezone,
            SalesForceId,
            SeasonName,
            Type,
            Tags,
            PropertyValues
        });
    }

    @handle(AtsOrganizationTagAction)
    private async handleTagApplicationsAction(action: AtsOrganizationTagAction) {
        await this.atsRepo.tag(action.selection.ids[0], {
            Selection: action.selection,
            AddTags: action.addTags,
            RemoveTags: action.removeTags
        });

        await dispatch(new CurrentAtsApplyTagAction(action.selection, action.addTags, action.removeTags));
    }

    private getPropertyValues = (propertyValues = []) => {
        return propertyValues.map((pv) => ({ FieldKey: pv.FieldKey, Value: pv.Value }));
    };

    @handle(UpdateOrganizationAction, FormErrorHandling)
    private async handleUpdateOrganization(action: UpdateOrganizationAction) {
        const propertyValues = this.getPropertyValues(action.form.PropertyValues);

        action.updated = await this.atsRepo.putOrganization(action.form?.Id, {
            Name: action.form.Name,
            Timezone: action.form.Timezone,
            Domain: action.form.Domain,
            Type: action.form.Type,
            SalesForceId: action.form.SalesForceId,
            Features: action.form.Features,
            PropertyValues: propertyValues
        });

        dispatch(new UpdatedOrganizationAction(action.updated));
    }

    public getAtsOrganizationFilter(): string {
        const { filter } = this.state;
        let filterExpression = AccountType[filter?.type] ? `metaData.type:${filter.type}` : "";
        if (filter?.tags) {
            filterExpression += filter.tags.map((tag) => `tags:(${tag})`).join(" AND ");
        }
        return filterExpression;
    }
}
